import { useState } from "react";
import { toast } from "sonner";
import { Award, Copy, ExternalLink } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { BACKEND_URL } from "@/lib/apiBase";

export default function BadgeEmbedDialog({ wineId, wineName }) {
  const [open, setOpen] = useState(false);
  const badgeUrl = `${BACKEND_URL}/api/wines/${wineId}/badge`;
  const wineUrl = `${BACKEND_URL.replace(/\/api$/, "")}/wines/${wineId}`;
  const embedHtml = `<a href="${wineUrl}" target="_blank" rel="noopener" title="${wineName} — AlfredWine.com Score">\n  <img src="${badgeUrl}" alt="${wineName} — AlfredWine.com Score" width="220" height="280" style="border:0;" />\n</a>`;

  const copyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(embedHtml);
      toast.success("Embed code copied");
    } catch {
      toast.error("Couldn't copy. Select and copy manually.");
    }
  };

  const copyImageUrl = async () => {
    try {
      await navigator.clipboard.writeText(badgeUrl);
      toast.success("Image URL copied");
    } catch {
      toast.error("Couldn't copy.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="badge-embed-btn"
          variant="outline"
          size="sm"
          className="border-gold/40 text-gold hover:bg-gold/10 h-9 gap-2 font-body text-xs"
        >
          <Award className="w-4 h-4" /> Get Badge
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-wine-paper border-white/10 max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white/90 font-display text-2xl">
            AlfredWine Score Badge
          </DialogTitle>
          <DialogDescription className="text-white/50 font-body">
            Embed this badge on your winery, importer or partner website.
            It always shows the live score for {wineName}.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-5 items-start mt-2">
          <div className="bg-black/40 rounded-sm p-3 flex items-center justify-center">
            <img
              src={badgeUrl}
              alt={`${wineName} — AlfredWine score`}
              width={220}
              height={280}
              data-testid="badge-preview"
              className="block"
            />
          </div>
          <div className="space-y-3">
            <div>
              <p className="font-body text-[10px] tracking-wider text-white/40 uppercase mb-1">
                HTML Embed Code
              </p>
              <textarea
                readOnly
                value={embedHtml}
                rows={5}
                onClick={(e) => e.target.select()}
                data-testid="badge-embed-code"
                className="w-full bg-black/40 border border-white/10 text-white/80 font-mono text-[11px] p-2 rounded-sm leading-relaxed"
              />
              <Button
                onClick={copyEmbed}
                data-testid="badge-copy-embed"
                className="w-full mt-2 bg-merlot hover:bg-merlot-light text-white gap-2 h-9 font-body text-xs"
              >
                <Copy className="w-3.5 h-3.5" /> Copy embed code
              </Button>
            </div>
            <div>
              <p className="font-body text-[10px] tracking-wider text-white/40 uppercase mb-1">
                Direct Image URL
              </p>
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={badgeUrl}
                  onClick={(e) => e.target.select()}
                  data-testid="badge-direct-url"
                  className="flex-1 bg-black/40 border border-white/10 text-white/70 font-mono text-[11px] px-2 h-8 rounded-sm"
                />
                <Button
                  onClick={copyImageUrl}
                  variant="outline"
                  size="icon"
                  data-testid="badge-copy-url"
                  className="h-8 w-8 border-white/10 text-white/60 hover:text-white"
                >
                  <Copy className="w-3.5 h-3.5" />
                </Button>
                <a href={badgeUrl} target="_blank" rel="noopener noreferrer">
                  <Button
                    variant="outline"
                    size="icon"
                    data-testid="badge-open"
                    className="h-8 w-8 border-white/10 text-white/60 hover:text-white"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
